import { registerSchema } from '@cambio/shared';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeftRight } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Field } from '@/components/ui/primitives';
import { ApiRequestError } from '@/lib/api';
import { useCurrencies, useRegister } from '@/lib/queries';

export const Route = createFileRoute('/register')({ component: Register });

function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const register = useRegister();
  const { data: currencies } = useCurrencies();

  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
    displayName: '',
    baseCurrency: 'CNY',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  const submit = async () => {
    setErrors({});
    // The same schema the API validates against, so the form cannot accept
    // something the server will refuse.
    const parsed = registerSchema.safeParse({
      ...form,
      displayName: form.displayName || undefined,
      locale: 'zh',
    });

    if (!parsed.success) {
      setErrors(
        Object.fromEntries(
          parsed.error.issues.map((issue) => [String(issue.path[0] ?? 'form'), issue.message]),
        ),
      );
      return;
    }

    try {
      await register.mutateAsync(parsed.data);
      await navigate({ to: '/app/ledger' });
    } catch (err) {
      setErrors({ form: err instanceof ApiRequestError ? err.message : t('errors.generic') });
    }
  };

  return (
    <div className="landing-shell flex min-h-dvh flex-col items-center justify-center bg-[var(--bg)] px-5 py-10">
      <div className="w-full max-w-sm">
        <Link
          to="/"
          className="mb-8 flex items-center justify-center gap-2.5 text-[19px] font-semibold tracking-tight"
        >
          <span className="brand-mark flex size-9 items-center justify-center rounded-[11px] bg-[var(--accent)] text-white">
            <ArrowLeftRight size={18} strokeWidth={2.35} />
          </span>
          <span>{t('common.appNameFull')}</span>
        </Link>

        <Card>
          <h1 className="text-[22px] font-bold">{t('auth.signUp')}</h1>

          <form
            className="mt-5 flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <Field
              label={t('auth.username')}
              autoComplete="username"
              autoCapitalize="none"
              value={form.username}
              onChange={(e) => set('username')(e.target.value)}
              {...(errors.username ? { error: errors.username } : {})}
            />
            <Field
              label={t('auth.email')}
              type="email"
              autoComplete="email"
              autoCapitalize="none"
              value={form.email}
              onChange={(e) => set('email')(e.target.value)}
              {...(errors.email ? { error: errors.email } : {})}
            />
            <Field
              label={t('auth.password')}
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => set('password')(e.target.value)}
              hint="至少 8 位"
              {...(errors.password ? { error: errors.password } : {})}
            />

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="base-currency"
                className="px-1 text-[13px] font-medium text-secondary"
              >
                {t('auth.baseCurrency')}
              </label>
              <select
                id="base-currency"
                value={form.baseCurrency}
                onChange={(e) => set('baseCurrency')(e.target.value)}
                className="h-12 rounded-field bg-[var(--fill-4)] px-3 text-[17px] outline-none"
              >
                {(currencies ?? []).map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.flag} {c.code} · {c.nameZh}
                  </option>
                ))}
              </select>
              {/* Explaining this here saves a support question later: it is a
                  default for new records, not a global switch. */}
              <p className="px-1 text-[13px] text-secondary">
                所有成本与盈亏都以本位币计量。之后可以改，历史记录不受影响。
              </p>
            </div>

            {errors.form && <p className="text-[13px] text-[var(--danger)]">{errors.form}</p>}

            <Button type="submit" variant="primary" size="lg" disabled={register.isPending}>
              {register.isPending ? `${t('common.loading')}…` : t('auth.signUp')}
            </Button>
          </form>
        </Card>

        <p className="pt-5 text-center text-[15px] text-secondary">
          {t('auth.hasAccount')}{' '}
          <Link to="/login" className="font-medium text-[var(--accent)]">
            {t('auth.signIn')}
          </Link>
        </p>
      </div>
    </div>
  );
}
